import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@utils/logger.util';
import { FatalFallback } from '@shared/components/FatalFallback/FatalFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('ErrorBoundary caught an error', { error, componentStack: info.componentStack });
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? <FatalFallback message={this.state.errorMessage ?? undefined} />
      );
    }
    return this.props.children;
  }
}
